// The member statuses a merchant can target, in display order. The strings are the backend's
// `lifecycle` cohort values, so they travel unchanged in the payload.
export const LIFECYCLE_AUDIENCES = [
  'lead',
  'active',
  'at_risk',
  'lapsed'
] as const;

export type LifecycleAudience = typeof LIFECYCLE_AUDIENCES[number];

export interface IOfferAudience {
  cohorts: Array<{ dimension: string; value: string }>;
  nonMembers: boolean;
}

export interface IAudienceSelection {
  statuses: LifecycleAudience[];
  nonMembers: boolean;
}

// A fresh object every time: Vue makes whatever it is handed reactive in place.
export function everyoneSelection(): IAudienceSelection {
  return { statuses: [...LIFECYCLE_AUDIENCES], nonMembers: true };
}

export function isEveryone(selection: IAudienceSelection): boolean {
  return (
    selection.nonMembers &&
    LIFECYCLE_AUDIENCES.every(status => selection.statuses.includes(status))
  );
}

export function isEmptySelection(selection: IAudienceSelection): boolean {
  return !selection.nonMembers && selection.statuses.length === 0;
}

// What the API stores: null is everyone.
export function toAudiencePayload(
  selection: IAudienceSelection
): IOfferAudience | null {
  if (isEveryone(selection)) return null;
  return {
    cohorts: LIFECYCLE_AUDIENCES.filter(status =>
      selection.statuses.includes(status)
    ).map(value => ({ dimension: 'lifecycle', value })),
    nonMembers: selection.nonMembers
  };
}

// A template or a stored offer may carry an audience; anything without one is everyone.
export function toAudienceSelection(
  audience?: IOfferAudience | null
): IAudienceSelection {
  if (!audience) return everyoneSelection();
  const values = (audience.cohorts || [])
    .filter(cohort => cohort.dimension === 'lifecycle')
    .map(cohort => cohort.value);
  return {
    statuses: LIFECYCLE_AUDIENCES.filter(status => values.includes(status)),
    nonMembers: !!audience.nonMembers
  };
}

// The nightly job clamps the merchant's inactivity window the same way before it sets a status.
export function clampStatusWindow(days?: number | null): number {
  const value = Number(days) || 30;
  return Math.min(Math.max(value, 14), 365);
}

// The day ranges behind each status, as the nightly job draws them for a window W: active under
// floor(W/2) days, at risk from floor(W/2) to W - 1, lapsed from W on.
export function statusDayRanges(windowDays: number) {
  const half = Math.floor(windowDays / 2);
  return {
    half,
    lastActive: half - 1,
    lastAtRisk: windowDays - 1,
    days: windowDays
  };
}
